import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SignIn } from "@phosphor-icons/react";
import { LogoMark } from "@/components/Logo";
import { api, useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { SITE } from "@/constants/site";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-sage border border-reed rounded-xl px-4 py-3.5 text-sm text-ink placeholder:text-moss/60 focus:outline-none focus:ring-2 focus:ring-mint/50 focus:border-mint transition-shadow duration-300";

  return (
    <div className="min-h-screen bg-sage flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-white rounded-3xl border border-black/5 shadow-xl shadow-forest/10 p-10">
        <div className="flex items-center gap-2.5">
          <LogoMark className="w-10 h-10" />
          <span className="font-serif text-xl font-semibold text-ink">{SITE.brand}</span>
        </div>
        <h1 className="mt-8 font-serif text-3xl text-ink">Advisor Login</h1>
        <p className="mt-2 text-sm text-moss">Sign in to view and manage enquiries.</p>

        <form onSubmit={submit} className="mt-8 flex flex-col gap-5" data-testid="admin-login-form">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address" className={inputCls} data-testid="admin-email-input" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" className={inputCls} data-testid="admin-password-input" />
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <motion.button type="submit" disabled={loading} whileTap={{ scale: 0.98 }}
            data-testid="admin-login-submit"
            className="bg-forest text-white font-semibold px-8 py-4 rounded-full hover:bg-forest-soft disabled:opacity-60 transition-colors duration-300 flex items-center justify-center gap-2">
            {loading ? "Signing in..." : "Sign In"} <SignIn size={16} weight="bold" />
          </motion.button>
        </form>

        <a href="/" className="mt-8 block text-center text-sm text-moss hover:text-forest transition-colors duration-300" data-testid="admin-back-home-link">
          ← Back to website
        </a>
      </motion.div>
    </div>
  );
}
